import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Users, FileText, Clock, AlertTriangle, 
  ChevronRight, Calendar, TrendingUp, Flag
} from 'lucide-react';
import { formatDate, getDaysUntil, getStatusClass } from '../lib/utils';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChartData = () => {
    if (!stats?.status_counts) return [];
    
    const relevantStatuses = [
      "Referral Received",
      "Assessment Scheduled",
      "Forms Sent",
      "Assessment Completed",
      "Report in Progress",
      "Report Sent",
      "Follow-up Completed"
    ];

    return relevantStatuses.map(status => ({
      name: status.replace(' Completed', '').replace('Received', 'Rcvd').replace('in Progress', 'WIP'),
      count: stats.status_counts[status] || 0,
      fullName: status
    }));
  };

  const chartColors = ['#94A3B8', '#3B82F6', '#F59E0B', '#8B5CF6', '#A855F7', '#10B981', '#14B8A6'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 font-['Manrope']">Dashboard</h1>
          <p className="text-slate-500 mt-1">
            {new Date().toLocaleDateString('en-AU', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric' 
            })}
          </p>
        </div>
        <Button 
          onClick={() => navigate('/clients/new')}
          className="bg-slate-800 hover:bg-slate-900"
          data-testid="add-client-btn"
        >
          Add New Client
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border border-slate-200 hover:shadow-md transition-shadow animate-fade-in">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Active Cases</p>
                <p className="text-3xl font-semibold text-slate-900 mt-1">{stats?.active_cases || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 hover:shadow-md transition-shadow animate-fade-in animate-fade-in-delay-1">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Reports Due</p>
                <p className="text-3xl font-semibold text-slate-900 mt-1">{stats?.reports_due || 0}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 hover:shadow-md transition-shadow animate-fade-in animate-fade-in-delay-2">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Forms Pending</p>
                <p className="text-3xl font-semibold text-slate-900 mt-1">{stats?.forms_pending || 0}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 hover:shadow-md transition-shadow animate-fade-in animate-fade-in-delay-3">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Overdue</p>
                <p className="text-3xl font-semibold text-red-600 mt-1">{stats?.overdue_count || 0}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <Card className="lg:col-span-2 border border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <TrendingUp className="w-5 h-5" />
              Cases by Stage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 w-full min-w-0">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={getChartData()} layout="vertical" margin={{ left: 20, right: 20, top: 5, bottom: 5 }}>
                  <XAxis type="number" hide />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    width={120}
                    tick={{ fontSize: 12, fill: '#64748B' }}
                  />
                  <Tooltip 
                    formatter={(value, name, props) => [value, props.payload.fullName]}
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {getChartData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <Calendar className="w-5 h-5" />
              Upcoming & Overdue
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Overdue */}
            {stats?.overdue_cases?.map((item) => (
              <div 
                key={item.id}
                className="p-3 bg-red-50 border border-red-100 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                onClick={() => navigate(`/clients/${item.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{item.name}</p>
                    <p className="text-sm text-red-600">{item.days_overdue} days overdue</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              </div>
            ))}

            {/* Upcoming */}
            {stats?.upcoming_deadlines?.map((item) => (
              <div 
                key={item.id}
                className="p-3 bg-amber-50 border border-amber-100 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors"
                onClick={() => navigate(`/clients/${item.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{item.name}</p>
                    <p className="text-sm text-amber-600">
                      {item.days_until === 0 ? 'Due today' : `Due in ${item.days_until} days`}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              </div>
            ))}

            {(!stats?.overdue_cases?.length && !stats?.upcoming_deadlines?.length) && (
              <p className="text-center text-slate-500 py-4">No upcoming deadlines</p>
            )}

            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => navigate('/clients')}
              data-testid="view-all-clients-btn"
            >
              View All Clients
            </Button>
          </CardContent>
        </Card>

        {/* Flagged for Supervision */}
        {stats?.flagged_clients?.length > 0 && (
          <Card className="lg:col-span-3 border border-purple-200 bg-purple-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Manrope'] text-purple-900">
                <Flag className="w-5 h-5 text-purple-600" />
                Flagged for Supervision ({stats.flagged_clients.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {stats.flagged_clients.map((client) => (
                  <div 
                    key={client.id}
                    className="p-3 bg-white border border-purple-200 rounded-lg cursor-pointer hover:border-purple-400 transition-colors"
                    onClick={() => navigate(`/clients/${client.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{client.name}</p>
                        <p className="text-sm text-slate-500">{client.assessment_type}</p>
                        <Badge className={`${getStatusClass(client.status)} mt-1 text-xs`}>
                          {client.status}
                        </Badge>
                      </div>
                      <Flag className="w-4 h-4 text-purple-500" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
